// src/pages/StateAdminReport/AllStateAdminForTransactions.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { formatDateTimeIST } from '../../utils/dateFormat';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const formatToIST = (dateStr) => formatDateTimeIST(dateStr);

// Helper to mask VPA for non-superadmin logins
const maskVpa = (vpa) => {
  if (!vpa || vpa === '-' || /^CASH-/i.test(vpa)) return '-'; // cash VPA never shown
  if (typeof vpa !== 'string' || !vpa.includes('@')) return vpa || 'N/A';
  const [username, domain] = vpa.split('@');
  if (username.length <= 4) {
    return `${username.slice(0, 2)}****@${domain}`;
  }
  return `${username.slice(0, 4)}****@${domain}`;
};

const AllStateAdminForTransactions = () => {
  const { can } = usePermissions();
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]

  // Date filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
        setUserId(decoded.userId);
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, []);

  const isSuperAdmin = userLevel === 1;

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/stateAdminReport/allStateHeadTransactions`,
        {
          startDate,
          endDate,
          userId,
          requesterLevel: userLevel || 1,
          paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (res.status === 200 && res.data) {
        let list = [];
        if (Array.isArray(res.data.transactions)) {
          list = res.data.transactions;
        } else if (res.data.data && Array.isArray(res.data.data.transactionsList)) {
          list = res.data.data.transactionsList;
        }
        // [CASH-MODE] client-side guard in case the API ignores the filter
        setTransactions(filterRowsByMode(list, paymentMode));
        setTotalsByMode(res.data.totalsByMode || (res.data.data && res.data.data.totalsByMode) || null);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching state head transactions:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch STATE HEAD transactions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userLevel !== null) {
      fetchTransactions();
    }
  }, [userLevel, startDate, endDate, paymentMode]); // [CASH-MODE] paymentMode

  // Search Filter by State Head ID / Name / Mobile / Email
  const filteredTransactions = useMemo(() => {
    if (!searchTerm.trim()) return transactions;
    const term = searchTerm.toLowerCase().trim();
    return transactions.filter((t) => {
      const shId = (t.stateHeadId || '').toLowerCase();
      const shName = (t.stateHeadName || '').toLowerCase();
      const shMobile = String(t.stateHeadMobile || '').toLowerCase();
      const shEmail = (t.stateHeadEmail || '').toLowerCase();
      const custName = (t.customerName || '').toLowerCase();
      const custId = (t.customerId || '').toLowerCase();

      return (
        shId.includes(term) ||
        shName.includes(term) ||
        shMobile.includes(term) ||
        shEmail.includes(term) ||
        custName.includes(term) ||
        custId.includes(term)
      );
    });
  }, [transactions, searchTerm]);

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('stateHead.transactions.export')) return;
    if (filteredTransactions.length === 0) return;

    const headers = [
      'SL No',
      'Date & Time',
      'State Head ID',
      'Q-Pay Center ID',
      'Merchant ID',
      'Customer ID',
      'Customer Name',
      'Customer VPA ID',
      'Type',
      'Mode', // [CASH-MODE]
      'Original Transaction Amount (₹)',
      'State Head Commission Amount (₹)',
      'Q-Pay Center Commission Amount (₹)'
    ];

    const csvRows = [headers.join(',')];

    filteredTransactions.forEach((t, idx) => {
      const formattedDate = formatToIST(t.date);
      const displayVpa = /^CASH-/i.test(t.customerVpa || '') ? '-' : (isSuperAdmin ? t.customerVpa : maskVpa(t.customerVpa));

      const row = [
        idx + 1,
        `"${formattedDate}"`,
        `"${t.stateHeadId || 'N/A'}"`,
        `"${t.qpayCentreId || 'N/A'}"`,
        `"${t.merchantId || 'N/A'}"`,
        `"${t.customerId || 'N/A'}"`,
        `"${t.customerName || 'N/A'}"`,
        `"${displayVpa}"`,
        `"${t.type || 'Credit'}"`,
        paymentModeLabel(t), // [CASH-MODE]
        t.originalAmount || 0,
        t.stateHeadCommission || 0,
        t.qpayCentreCommission || 0
      ];

      csvRows.push(row.join(','));
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `STATE_HEAD_Transaction_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // [CASH-MODE] API totals (ignores search), else the visible rows
  const modeTotals = (!searchTerm.trim() && totalsByMode) || totalsByModeFromRows(filteredTransactions, (t) => t.originalAmount);
  const modeAmountKey = modeTotals?.UPI?.originalAmount !== undefined ? 'originalAmount' : 'amount';

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <BackButton />
      <br /><br />

      {/* Header & Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          STATE HEAD Transaction Report
        </Typography>

        {/* CSV Export (permission gated) */}
        {can('stateHead.transactions.export') && (
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={filteredTransactions.length === 0}
          >
            Export CSV
          </Button>
        )}
      </Box>

      {/* Filters Bar: Date Wise + Search */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Search Input */}
          <Box sx={{ flex: 1, minWidth: 250, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="action" />
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search by State Head ID, Name, Mobile, or Email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>

          {/* Date Filters */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              label="From Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* [CASH-MODE] */}
          <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} />
        </Box>
      </Paper>

      {/* [CASH-MODE] */}
      <PaymentModeTotals totals={modeTotals} amountKey={modeAmountKey} title="Transactions" sx={{ mb: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, maxHeight: 'calc(100vh - 260px)', minHeight: 450, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SL No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date & Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>State Head ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Q-Pay Center ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Merchant ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer VPA ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Mode</TableCell>{/* [CASH-MODE] */}
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Original Transaction Amount (₹)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>State Head Commission (₹)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Q-Pay Center Commission (₹)</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                    No State Head transaction records found for the selected date range.
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransactions.map((t, idx) => {
                  const formattedDate = formatToIST(t.date);
                  const displayVpa = /^CASH-/i.test(t.customerVpa || '') ? '-' : (isSuperAdmin ? t.customerVpa : maskVpa(t.customerVpa));

                  return (
                    <TableRow key={t._id || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{formattedDate}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {t.stateHeadId || 'N/A'}
                      </TableCell>
                      <TableCell>{t.qpayCentreId || 'N/A'}</TableCell>
                      <TableCell>{t.merchantId || 'N/A'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t.customerId || 'N/A'}</TableCell>
                      <TableCell>{t.customerName || 'N/A'}</TableCell>
                      <TableCell>{displayVpa}</TableCell>
                      <TableCell>
                        <Chip
                          label={t.type || 'Credit'}
                          size="small"
                          color={t.type === 'Debit' ? 'error' : 'success'}
                          variant="outlined"
                        />
                      </TableCell>
                      {/* [CASH-MODE] */}
                      <TableCell>
                        <PaymentModeChip row={t} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>₹{t.originalAmount ? t.originalAmount.toLocaleString() : '0'}</TableCell>
                      <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>₹{t.stateHeadCommission || '0'}</TableCell>
                      <TableCell sx={{ color: 'info.main', fontWeight: 'bold' }}>₹{t.qpayCentreCommission || '0'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AllStateAdminForTransactions;